import React from "react";

/**
 * Small stat card used in the top metrics grid.
 *
 * @param {string} label - Uppercase label, e.g. "Total OT Hours"
 * @param {React.ReactNode} icon - Icon element (e.g. from lucide-react)
 * @param {string|number} value - Main value to display
 * @param {string} [unit] - Optional trailing unit, e.g. "hrs"
 * @param {"default"|"amber"|"emerald"|"red"} [tone] - Color treatment
 */
export default function MetricCard({ label, icon, value, unit, tone = "default" }) {
  const toneStyles = {
    default: {
      card: "bg-white border-surface-200 hover:border-primary-400",
      label: "text-surface-500",
      value: "text-primary-600",
      icon: "text-primary-500",
      iconBg: "bg-primary-50",
    },
    amber: {
      card: "bg-amber-50 border-amber-200 hover:border-amber-400",
      label: "text-amber-700",
      value: "text-amber-800",
      icon: "text-amber-600",
      iconBg: "bg-amber-100",
    },
    emerald: {
      card: "bg-emerald-50 border-emerald-200 hover:border-emerald-400",
      label: "text-emerald-700",
      value: "text-emerald-800",
      icon: "text-emerald-600",
      iconBg: "bg-emerald-100",
    },
    red: {
      card: "bg-red-50 border-red-200 hover:border-red-400",
      label: "text-red-700",
      value: "text-red-800",
      icon: "text-red-600",
      iconBg: "bg-red-100",
    },
  };

  const styles = toneStyles[tone] ?? toneStyles.default;

  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-3 shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${styles.card}`}
    >
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-bold uppercase tracking-widest ${styles.label}`}>
          {label}
        </span>
        <span className={`w-8 h-8 flex items-center justify-center rounded-lg ${styles.iconBg} ${styles.icon}`}>
          {icon}
        </span>
      </div>
      <div className={`text-2xl font-bold leading-tight ${styles.value}`}>
        {value}
        {unit && (
          <span className={`text-sm font-medium ml-1 opacity-70 ${styles.label}`}>
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}